import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Autocomplete, Box, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Project } from '../../types/projectTypes';
import { getActiveProjects } from '../../api/projectApi';
import { getProjectActivities } from '../../api/activityApi';
import { ProjectActivity } from '../../types/ActivityTypes';
import { getTowersByProjectId } from '../../api/towerApi';
import { Tower } from '../../types/towerTypes';
import { getProductionControl } from '../../api/productionControlApi';
import { format } from "date-fns";
import ExcelJS from 'exceljs';
import logo from '../../assets/images/logoElecnor.png';
import { getUserProject } from '../../api/userApi';
import { profiles, ADMIN_SISTEMA } from "../../contexts/constants/profiles";
import { classifyActivity } from '../../utils/activityGoals';

// Fases que llevan peso (deben sumar 100% cada una) y su etiqueta para el % acumulado.
const PHASE_DEFS: { cat: string; label: string }[] = [
    { cat: 'OBRAS_CIVILES', label: 'Obras Civiles' },
    { cat: 'MONTAJE', label: 'Montaje' },
    { cat: 'TENDIDO_CONDUCTOR', label: 'Tendido Conductor' },
    { cat: 'TENDIDO_OPGW', label: 'Tendido OPGW' },
];

// --- UTILIDADES (Se mantienen igual) ---
const cleanText = (text: string | undefined | null) => {
    return (text || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "").trim();
};

const normalizeUnit = (unit: string | undefined | null): string => {
    if (!unit) return "";
    return unit.replace(/m3/gi, "m³");
};

const parseSafeFloat = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value;
    const cleanStr = String(value).replace(',', '.');
    const num = parseFloat(cleanStr);
    return isNaN(num) ? 0 : num;
};

const formatNumber = (value: number | string | undefined | null, decimals: number = 2): string => {
    if (value === null || value === undefined || value === '') return "";
    const num = parseSafeFloat(value);
    if (num === 0 && value !== 0 && value !== "0") return String(value);
    return num.toFixed(decimals);
};

const naturalSort = (a: string | undefined, b: string | undefined) => {
    const strA = a || "";
    const strB = b || "";
    return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
};

const formatDate = (date: string | null): string => {
    if (!date) return "";
    return format(new Date(date), "dd/MM/yy");
};

// --- LÓGICA VNT ---
const findConcreteVnt = (
    excavationSub: string | undefined,
    excavationName: string | undefined,
    towerNumber: string,
    towerActivities: any[],
    allColumns: ProjectActivity[]
) => {
    const cleanSub = cleanText(excavationSub);
    const cleanName = cleanText(excavationName).replace("excavacion", "").trim();
    const searchToken = cleanSub || cleanName;
    if (!searchToken) return null;

    const candidates = towerActivities.map(dataItem => {
        const def = allColumns.find(col => col.activity.id === dataItem.id);
        if (!def) return null;
        const name = cleanText(def.activity.name);
        if (!name.includes("hormigonado") || name.includes("emplantillado") || name.includes("limpieza")) {
            return null;
        }
        return {
            rawVnt: dataItem.naturalGroundVolumeVnt,
            value: parseSafeFloat(dataItem.naturalGroundVolumeVnt),
            fullName: name + " " + cleanText(def.activity.subActivity)
        };
    }).filter(item => item !== null) as any[];

    if (candidates.length === 0) return null;

    let matches = candidates.filter(item => {
        return item.fullName.includes(searchToken) || searchToken.includes(item.fullName);
    });

    if (matches.length === 0) matches = candidates; // Fallback
    matches.sort((a, b) => b.value - a.value);

    return matches[0].rawVnt;
};

// --- TIPOS ---
type ActivityDisplayType = 'HORMIGONADO' | 'NO_REAL' | 'DATES_ONLY' | 'WITH_LEADER' | 'LEADER_NO_TEORIC' | 'STANDARD_REAL' | 'SIMPLE';

const getActivityType = (activity: any): ActivityDisplayType => {
    const name = activity.name || "";
    const cleanName = cleanText(name);
    // Extraemos y limpiamos la fase/sección
    const section = activity.section || "";
    const cleanSection = cleanText(section);
    const sub = activity.subActivity || "";
    const unit = activity.unitOfMeasurement || "NA";

    if (cleanName.includes("hormigonado")) return 'HORMIGONADO';
    if (cleanSection.includes("tendido") || cleanName.includes("tendido") || cleanName.includes("flechado") || cleanName.includes("rematado")) {
        return 'LEADER_NO_TEORIC';
    }

    // Mantenemos las demás validaciones por si hay otras actividades sin sección
    const leaderNoTeoricKeywords = ["Instalación de separadores", "Instalación de Puentes", "Instalación de Balizas", "Revisado de Torres"];
    if (leaderNoTeoricKeywords.some(k => name.includes(k))) return 'LEADER_NO_TEORIC';

    if (["Clasificación", "Acopio", "Prearmado", "Pre-armado"].some(t => name.includes(t))) return 'SIMPLE';

    // Las de Tendido ya fueron atrapadas arriba, así que solo queda Izado para WITH_LEADER (que sí muestra Teórico)
    if (sub.includes("Izado")) return 'WITH_LEADER';

    if (unit !== "NA") return 'STANDARD_REAL';
    return 'SIMPLE';
};

const getColSpan = (activity: any): number => {
    const type = getActivityType(activity);
    switch (type) {
        case 'HORMIGONADO': return 7;
        case 'WITH_LEADER': return 4;
        case 'STANDARD_REAL': return 4;
        case 'NO_REAL': return 3;
        case 'LEADER_NO_TEORIC': return 3;
        default: return 2;
    }
};

const COL_WIDTHS = { vertex: 50, tower: 60, type: 60, span: 55 };
const COL_POS = {
    vertex: 0,
    tower: COL_WIDTHS.vertex,
    type: COL_WIDTHS.vertex + COL_WIDTHS.tower,
    span: COL_WIDTHS.vertex + COL_WIDTHS.tower + COL_WIDTHS.type
};

const tableStyles = {
    headerFont: {
        fontSize: "0.65rem", fontWeight: "bold", padding: "0px 1px", lineHeight: "1.1",
        boxSizing: "border-box" as const, textAlign: "center" as const, verticalAlign: "middle",
        whiteSpace: "normal" as const, wordWrap: "break-word" as const
    },
    bodyFont: {
        fontSize: "0.65rem", padding: "1px 2px", whiteSpace: "normal" as const,
        wordBreak: "break-word" as const, textAlign: "center" as const, verticalAlign: "middle"
    },
    stickyColumnHead: {
        position: "sticky" as const, zIndex: 10, backgroundColor: "#78a1bf", color: "#004F86",
        borderRight: "1px solid #ccc", verticalAlign: "middle", textAlign: "center" as const
    },
    stickyColumnBody: {
        position: "sticky" as const, zIndex: 9, backgroundColor: "#ffffff",
        borderRight: "1px solid #ccc", verticalAlign: "middle", textAlign: "center" as const
    }
};

const ProductionControl: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>();
    const [activitiesProject, setActivitiesProject] = useState<ProjectActivity[]>([]);
    const [towersList, setTowerData] = useState<Tower[]>([]);
    const [controlProductionData, setcontrolProductionData] = useState<any[]>([]);
    const [filteredControlProduction, setFilteredControlProduction] = useState<any[]>([]);
    const [selectedTower, setSelectedTower] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [userRole, setUserRole] = useState<string>("");
    const rightTableRefs = useRef<(HTMLTableRowElement | null)[]>([]);

    useEffect(() => { fetchProjects(); }, []);
    useEffect(() => { if (selectedProject) { fetchActivitiesData(); } }, [selectedProject]);

    const fetchProjects = async () => {
        try {
            const userStr = localStorage.getItem("user");
            let role = "";
            let rut = "";
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
                setUserRole(role);
            }
            let projectsData: Project[] = [];
            // Director, Gerente, Visualizador y Admin de Sistema ven los proyectos activos del país
            // seleccionado (el backend los acota con la cabecera X-Country-Id).
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name || role === ADMIN_SISTEMA) {
                projectsData = await getActiveProjects();
            } else {
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }
            const validProjects = projectsData.filter((p) => p.status === undefined || p.status === true);
            setProjects(validProjects);
        } catch (error) {
            console.error("Error al cargar proyectos:", error);
            setProjects([]);
        }
    };

    const fetchActivitiesData = async () => {
        setLoading(true);
        try {
            const projectId = selectedProject || 0;

            const [columnsActivities, fetchedTowers, fetchControlProduction] = await Promise.all([
                getProjectActivities(projectId),
                getTowersByProjectId(projectId),
                getProductionControl(projectId)
            ]);

            let filtered = columnsActivities.filter((item) => item.activity.addProduction);

            filtered.sort((a, b) => {
                const sectionA = a.activity.section || '';
                const sectionB = b.activity.section || '';
                const nameA = a.activity.name || '';
                const nameB = b.activity.name || '';

                if (sectionA !== sectionB || nameA !== nameB) {
                    return (a.order || 9999) - (b.order || 9999);
                }

                const subA = a.activity.subActivity || "";
                const subB = b.activity.subActivity || "";
                const regex = /circuito\s*(\d+).*fase\s*(\d+)/i;
                const matchA = subA.match(regex);
                const matchB = subB.match(regex);

                if (matchA && matchB) {
                    const circA = parseInt(matchA[1], 10);
                    const circB = parseInt(matchB[1], 10);
                    const faseA = parseInt(matchA[2], 10);
                    const faseB = parseInt(matchB[2], 10);
                    if (circA !== circB) return circA - circB;
                    return faseA - faseB;
                }
                return naturalSort(subA, subB);
            });

            setActivitiesProject(filtered);
            setTowerData(fetchedTowers);
            setcontrolProductionData(fetchControlProduction);
            setFilteredControlProduction(fetchControlProduction);
        } finally { setLoading(false); }
    };

    const handleProjectChange = (e: SelectChangeEvent<number>) => { const projectId = Number(e.target.value); setSelectedProject(projectId); setSelectedTower(undefined); };

    const handleTowerChange = (event: any, value: string | null) => {
        setSelectedTower(value || '');
        if (value) {
            const filteredData = controlProductionData.filter((data) => data.towerNumber === value); setFilteredControlProduction(filteredData);
        } else { setFilteredControlProduction(controlProductionData); }
    };

    const isRoadActivity = (activity: any) => {
        const rawString = JSON.stringify(activity).toLowerCase();
        return rawString.includes("camino") || rawString.includes("acceso");
    };

    const getRoadValue = (towerNumber: string | number, columnActivity: any) => {
        if (!towersList || towersList.length === 0) return 0;
        const targetNum = parseFloat(String(towerNumber));
        const fullTower = towersList.find(t => parseFloat(String(t.towerNumber)) === targetNum);
        if (!fullTower) return 0;

        const colName = cleanText(columnActivity.name);
        const subActivity = cleanText(columnActivity.subActivity);
        const fullName = `${colName} ${subActivity}`;
        const data = fullTower as any;

        if (fullName.includes("plano") || fullName.includes("liviano")) return data.lightRoadLength || 0;
        if (fullName.includes("medio")) return data.mediumRoadLength || 0;
        if (fullName.includes("fuerte") || fullName.includes("pesado")) return data.heavyRoadLength || 0;
        return 0;
    };

    const processedDataMap = useMemo(() => {
        const map = new Map();
        if(!filteredControlProduction.length || !activitiesProject.length) return map;

        filteredControlProduction.forEach(towerRow => {
            activitiesProject.forEach(column => {
                const uniqueKey = `${towerRow.towerNumber}-${column.id}`;
                const matchingActivity = towerRow.activitis?.find((a: any) => a.id === column.activity.id);

                const allMatches = towerRow.activitis?.filter((a: any) => a.id === column.activity.id) || [];
                const totalVnt = allMatches.reduce((acc: number, curr: any) => acc + parseSafeFloat(curr.naturalGroundVolumeVnt), 0);
                const totalOutcrop = allMatches.reduce((acc: number, curr: any) => acc + parseSafeFloat(curr.outcropVolume), 0);

                const type = getActivityType(column.activity);
                const unit = column.activity.unitOfMeasurement;
                const rawTeoric = matchingActivity?.teoric;
                const actNameClean = cleanText(column.activity.name);
                const isRoads = actNameClean.includes("camino") || actNameClean.includes("acceso");
                const isExcavation = actNameClean.includes("excavaci");

                let finalTeoric = 0;
                let realToDisplay: any = matchingActivity?.real;
                let realHormigonado = 0;

                if (column.activity.name === "Tendido OPGW") {
                    finalTeoric = towerRow.forwardSpanTeoric ? (Number(towerRow.forwardSpanTeoric) / 1000) : 0;
                } else if (isRoads) {
                    finalTeoric = getRoadValue(towerRow.towerNumber, column.activity);
                    realToDisplay = matchingActivity?.realAdvance ?? matchingActivity?.real ?? 0;
                } else {
                    finalTeoric = (unit === 'tn' && rawTeoric) ? (rawTeoric / 1000) : rawTeoric;
                }

                if (isExcavation) {
                    const vntValue = findConcreteVnt(
                        column.activity.subActivity,
                        column.activity.name,
                        towerRow.towerNumber,
                        towerRow.activitis,
                        activitiesProject
                    );
                    if (vntValue !== null && vntValue !== undefined && vntValue !== "") {
                        realToDisplay = vntValue;
                    }
                }

                if (type === 'HORMIGONADO') {
                    realHormigonado = (totalVnt + totalOutcrop) > 0 ? (totalVnt + totalOutcrop) : parseSafeFloat(matchingActivity?.real);
                }

                map.set(uniqueKey, {
                    startDate: matchingActivity?.startDate,
                    endDate: matchingActivity?.endDate,
                    finalTeoric,
                    realToDisplay,
                    realHormigonado,
                    totalVnt,
                    totalOutcrop,
                    frontLeader: matchingActivity?.frontLeader,
                    isRoads,
                    isExcavation,
                    type,
                    displayUnit: unit
                });
            });
        });
        return map;
    }, [filteredControlProduction, activitiesProject, towersList]);

    // --- CONTEO DE REGISTROS POR COLUMNA (Ignorando ceros) ---
    const columnCounts = useMemo(() => {
        const counts = new Map();
        if (!filteredControlProduction.length || !activitiesProject.length) return counts;

        activitiesProject.forEach((column) => {
            let startCount = 0, endCount = 0, teoricCount = 0, realCount = 0, vntCount = 0, afloraCount = 0, leaderCount = 0;
            // Sumatorias de los valores productivos (Teórico, Real, VNT, VAflora).
            let teoricSum = 0, realSum = 0, vntSum = 0, afloraSum = 0;
            const type = getActivityType(column.activity);

            filteredControlProduction.forEach((towerRow) => {
                const uniqueKey = `${towerRow.towerNumber}-${column.id}`;
                const cellData = processedDataMap.get(uniqueKey);

                if (cellData) {
                    if (cellData.startDate) startCount++;
                    if (cellData.endDate) endCount++;

                    // Ignorar 0 en valores teóricos
                    if (cellData.finalTeoric !== null && cellData.finalTeoric !== undefined && cellData.finalTeoric !== "" && Number(cellData.finalTeoric) !== 0) teoricCount++;
                    teoricSum += parseSafeFloat(cellData.finalTeoric);

                    // Ignorar 0 en valores reales
                    if (type === 'HORMIGONADO') {
                        if (cellData.realHormigonado && Number(cellData.realHormigonado) !== 0) realCount++;
                        realSum += parseSafeFloat(cellData.realHormigonado);
                    } else {
                        if (cellData.realToDisplay !== null && cellData.realToDisplay !== undefined && cellData.realToDisplay !== "" && Number(cellData.realToDisplay) !== 0) realCount++;
                        realSum += parseSafeFloat(cellData.realToDisplay);
                    }

                    if (cellData.totalVnt && cellData.totalVnt > 0) vntCount++;
                    if (cellData.totalOutcrop && cellData.totalOutcrop > 0) afloraCount++;
                    if (cellData.frontLeader && cellData.frontLeader.length > 0) leaderCount++;
                    vntSum += parseSafeFloat(cellData.totalVnt);
                    afloraSum += parseSafeFloat(cellData.totalOutcrop);
                }
            });

            counts.set(column.id, { startCount, endCount, teoricCount, realCount, vntCount, afloraCount, leaderCount, teoricSum, realSum, vntSum, afloraSum, type });
        });
        return counts;
    }, [filteredControlProduction, activitiesProject, processedDataMap]);

    // --- AVANCE ACUMULADO POR FASE (categoría validada, normalizado por Σpeso) ---
    // Σ(%avance Término × peso) / Σ(peso), con %avance = torres con Fecha de Término / total.
    const phaseProgress = useMemo(() => {
        const total = filteredControlProduction.length;
        const out: { label: string; pct: number }[] = [];
        if (!total || !activitiesProject.length) return out;
        PHASE_DEFS.forEach((pd) => {
            const cols = activitiesProject.filter(
                (c) => classifyActivity(c.activity) === pd.cat && Number(c.weightDistribution || 0) > 0
            );
            if (!cols.length) return;
            const sumWd = cols.reduce((s, c) => s + Number(c.weightDistribution || 0), 0);
            const weighted = cols.reduce(
                (s, c) => s + ((columnCounts.get(c.id)?.endCount || 0) / total) * Number(c.weightDistribution || 0),
                0
            );
            out.push({ label: pd.label, pct: sumWd > 0 ? weighted / sumWd : 0 });
        });
        return out;
    }, [filteredControlProduction, activitiesProject, columnCounts]);

    // --- COLOR POR SEMANA (mismo criterio que el Excel) ---
    // Semana actual = semana ISO (lun-dom) que contiene hoy -> amarillo suave.
    // Semana anterior = exactamente 1 semana hacia atrás -> azul suave. El resto sin color.
    const weekColorOf = useMemo(() => {
        const stripTime = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
        const startOfISOWeek = (d: Date) => { const x = stripTime(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
        const cur = startOfISOWeek(new Date()).getTime();
        const prev = cur - 7 * 86400000;
        const next = cur + 7 * 86400000;
        return (raw: any): string | undefined => {
            if (!raw) return undefined;
            const t = stripTime(new Date(raw)).getTime();
            if (isNaN(t)) return undefined;
            if (t >= cur && t < next) return '#FFF2CC';   // semana actual (amarillo suave)
            if (t >= prev && t < cur) return '#DDEBF7';   // semana anterior (azul suave)
            return undefined;
        };
    }, []);

    // --- CONTEO DE COLUMNAS PRINCIPALES (Vértice, Tipo, Vano) ---
    const { vertexCount, spanTotal, towerCount } = useMemo(() => {
        let vCount = 0, sTotal = 0, wCount = 0;

        filteredControlProduction.forEach((t) => {
            const v = String(t.vertexName || "").trim();
            const span = parseSafeFloat(t.forwardSpanTeoric);
            const weight = parseSafeFloat(t.towerWeightTeoric);

            // Evitamos contar vacíos y guiones
            if (v !== "" && v !== "-") vCount++;
            // "Vano Adelante": total de metros de vano de los registros (suma, no conteo)
            sTotal += span;
            // Conteo de torres: solo las que poseen "peso torre" (tower_weight) > 0
            if (weight !== 0) wCount++;
        });

        return { vertexCount: vCount, spanTotal: sTotal, towerCount: wCount };
    }, [filteredControlProduction]);

    const sectionHeaders = useMemo(() => {
        return activitiesProject.reduce((sections, column) => {
            const existingSection = sections.find((section) => section.name === column.activity.section);
            const span = getColSpan(column.activity);
            if (existingSection) { existingSection.colSpan += span; } else { sections.push({ name: column.activity.section || "", colSpan: span }); }
            return sections;
        }, [] as { name: string; colSpan: number }[]);
    }, [activitiesProject]);

    const activityHeaders = useMemo(() => {
        return activitiesProject.reduce((acc, column) => {
            const span = getColSpan(column.activity);
            const last = acc[acc.length - 1];
            if (last && last.name === column.activity.name && last.section === column.activity.section) {
                last.colSpan += span;
            } else {
                acc.push({ id: column.id, name: column.activity.name, section: column.activity.section, colSpan: span });
            }
            return acc;
        }, [] as { id: number, name?: string, section?: string, colSpan: number }[]);
    }, [activitiesProject]);

    const exportToExcel = async () => {
            if (!activitiesProject.length) return;
            const workbook = new ExcelJS.Workbook();
            // Forzar el recálculo de todas las fórmulas al abrir el archivo (ExcelJS no guarda
            // los valores cacheados; sin esto algunos visores muestran 0/vacío en los totales).
            workbook.calcProperties.fullCalcOnLoad = true;
            const worksheet = workbook.addWorksheet('Control de Producción');

            // --- UTILIDADES DE EXPORTACIÓN ---
            const BASE_COLS = 4; // Vértice, Torre, Tipo Torre, Vano Adelante
            const colLetter = (n: number): string => {
                let s = '';
                while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
                return s;
            };

            // Colores de relleno por semana (rellenos suaves solicitados por Pedro Mira).
            const SOFT_YELLOW = 'FFFFF2CC'; // Semana actual
            const SOFT_BLUE = 'FFDDEBF7';   // Semana anterior (modificable)
            const stripTime = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
            const startOfISOWeek = (d: Date) => { const x = stripTime(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
            const consultDate = new Date(); // Fecha de consulta del archivo
            const curWeekStart = startOfISOWeek(consultDate).getTime();
            const prevWeekStart = curWeekStart - 7 * 86400000;
            const nextWeekStart = curWeekStart + 7 * 86400000;
            const weekBucket = (raw: any): 'current' | 'previous' | null => {
                if (!raw) return null;
                const t = stripTime(new Date(raw)).getTime();
                if (isNaN(t)) return null;
                if (t >= curWeekStart && t < nextWeekStart) return 'current';
                if (t >= prevWeekStart && t < curWeekStart) return 'previous';
                return null;
            };
            // Posición absoluta (1-based) de las columnas Inicio/Término de cada actividad.
            // El ancho de bloque por tipo coincide con getColSpan (2 a 7 columnas).
            let runningCol = BASE_COLS;
            const activityCols = activitiesProject.map((column) => {
                const type = getActivityType(column.activity);
                const span = getColSpan(column.activity);
                const inicioCol = runningCol + 1;
                runningCol += span;
                // Offsets (dentro del bloque) de las columnas con valores productivos que se SUMAN:
                // HORMIGONADO -> Teórico, Real, VNT, VAflora; STANDARD_REAL -> Teórico, Real;
                // NO_REAL / WITH_LEADER -> Teórico(/Peso). El resto (fechas, "Realizó") se cuentan.
                const numericOffsets = type === 'HORMIGONADO' ? [2, 3, 4, 5]
                    : type === 'STANDARD_REAL' ? [2, 3]
                    : type === 'NO_REAL' ? [2]
                    : type === 'WITH_LEADER' ? [2]
                    : [];
                return {
                    column, type, span, inicioCol, terminoCol: inicioCol + 1, endCol: inicioCol + span - 1,
                    numericCols: numericOffsets.map((o) => inicioCol + o),
                    category: classifyActivity(column.activity),
                };
            });

            // Avance acumulado por FASE (categoría validada), normalizado por Σpeso:
            // Σ(%avance Término × peso) / Σ(peso), sobre las actividades de esa categoría con peso > 0.
            const phaseGroups = PHASE_DEFS.map((pd) => ({
                label: pd.label,
                cols: activityCols.filter((ac) => ac.category === pd.cat && Number(ac.column.weightDistribution || 0) > 0),
            })).filter((g) => g.cols.length > 0);

            try {
                const imageResponse = await fetch(logo);
                const imageBuffer = await imageResponse.arrayBuffer();
                const imageId = workbook.addImage({ buffer: imageBuffer, extension: 'png' });
                worksheet.addImage(imageId, { tl: { col: 0, row: 1 }, ext: { width: 160, height: 60 } });
            } catch (e) {
                console.warn("No se pudo cargar el logo para la exportación");
            }

            const projectName = projects.find(project => project.id === selectedProject);
            if (projectName) {
                const projectCell = worksheet.getCell('D2');
                projectCell.value = `PROYECTO: ${projectName.name?.toUpperCase() || ''}`;
                projectCell.font = { bold: true, size: 18, color: { argb: 'FF004F86' } };
                projectCell.alignment = { horizontal: 'left', vertical: 'middle' };
            }

            worksheet.addRow([]); // Área del logo
            worksheet.addRow([]); // Área del logo

            // Fila de leyenda: fecha de consulta + código de colores por semana.
            const legendRow = worksheet.addRow([]);
            legendRow.getCell(1).value = `Fecha de consulta: ${format(consultDate, 'dd/MM/yyyy')}`;
            legendRow.getCell(1).font = { italic: true, bold: true, size: 11, color: { argb: 'FF004F86' } };
            const yellowLegend = legendRow.getCell(6);
            yellowLegend.value = 'Semana actual';
            yellowLegend.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SOFT_YELLOW } };
            yellowLegend.alignment = { horizontal: 'center', vertical: 'middle' };
            const blueLegend = legendRow.getCell(8);
            blueLegend.value = 'Semana anterior';
            blueLegend.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SOFT_BLUE } };
            blueLegend.alignment = { horizontal: 'center', vertical: 'middle' };

            // Fila de AVANCE PONDERADO POR FASE (peso asignado por el jefe de proyecto).
            // Las fórmulas se rellenan más abajo, cuando se conoce la fila de % de avance.
            const rollupRow = worksheet.addRow([]);
            worksheet.mergeCells(`A${rollupRow.number}:D${rollupRow.number}`);
            rollupRow.getCell(1).value = 'AVANCE PONDERADO (peso JP):';
            rollupRow.getCell(1).font = { bold: true, size: 10, color: { argb: 'FF004F86' } };
            rollupRow.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };

            const headerRow1 = ['Vértice', 'Torre', 'Tipo Torre', 'Vano Adelante'];
            const headerRow2 = ['', '', '', ''];

            activitiesProject.forEach((column) => {
                const type = getActivityType(column.activity);
                const actName = `${column.activity.name} - ${column.activity.subActivity || ''}`;

                const addHeaders = (cols: string[]) => {
                    cols.forEach(col => {
                        headerRow1.push(actName);
                        headerRow2.push(col);
                    });
                };

                switch (type) {
                    case 'HORMIGONADO':
                        addHeaders(['Inicio', 'Término', 'Teórico', 'Real', 'VNT', 'VAflora', 'Realizó']); break;
                    case 'STANDARD_REAL':
                        addHeaders(['Inicio', 'Término', 'Teórico', 'Real']); break;
                    case 'NO_REAL':
                        addHeaders(['Inicio', 'Término', 'Teórico']); break;
                    case 'WITH_LEADER':
                        addHeaders(['Inicio', 'Término', 'Teórico/Peso', 'Realizó']); break;
                    case 'LEADER_NO_TEORIC':
                        addHeaders(['Inicio', 'Término', 'Realizó']); break;
                    default:
                        addHeaders(['Inicio', 'Término']); break;
                }
            });

            const h1 = worksheet.addRow(headerRow1);
            const h2 = worksheet.addRow(headerRow2);

            [h1, h2].forEach(row => {
                row.eachCell(cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF78A1BF' } };
                    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                    cell.border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
                });
            });

            // --- INICIO FILA DE TOTALES EN EXCEL ---
            if (filteredControlProduction.length > 0) {
                const totalsRowData: any[] = [
                    vertexCount,
                    towerCount, // Solo torres con "peso torre" (tower_weight) > 0
                    "",         // "Tipo Torre" sin conteo (removido)
                    spanTotal   // "Vano Adelante": total de metros (suma), no conteo
                ];

                activitiesProject.forEach((column) => {
                    const counts = columnCounts.get(column.id) || {};
                    const type = counts.type || getActivityType(column.activity);

                    switch (type) {
                        case 'HORMIGONADO':
                            totalsRowData.push(counts.startCount || 0, counts.endCount || 0, counts.teoricCount || 0, counts.realCount || 0, counts.vntCount || 0, counts.afloraCount || 0, counts.leaderCount || 0);
                            break;
                        case 'STANDARD_REAL':
                            totalsRowData.push(counts.startCount || 0, counts.endCount || 0, counts.teoricCount || 0, counts.realCount || 0);
                            break;
                        case 'NO_REAL':
                            totalsRowData.push(counts.startCount || 0, counts.endCount || 0, counts.teoricCount || 0);
                            break;
                        case 'WITH_LEADER':
                            totalsRowData.push(counts.startCount || 0, counts.endCount || 0, counts.teoricCount || 0, counts.leaderCount || 0);
                            break;
                        case 'LEADER_NO_TEORIC':
                            totalsRowData.push(counts.startCount || 0, counts.endCount || 0, counts.leaderCount || 0);
                            break;
                        default:
                            totalsRowData.push(counts.startCount || 0, counts.endCount || 0);
                            break;
                    }
                });

                // ORDEN: primero la fila de % DE AVANCE (arriba), luego Torres completadas (abajo).
                const pctRow = worksheet.addRow([]);
                pctRow.getCell(3).value = '% DE AVANCE';
                pctRow.eachCell({ includeEmpty: false }, cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F0F8' } };
                    cell.font = { bold: true, color: { argb: 'FF004F86' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = { top: {style:'medium', color:{argb:'FF999999'}}, left: {style:'thin', color:{argb:'FFCCCCCC'}}, bottom: {style:'thin', color:{argb:'FFCCCCCC'}}, right: {style:'thin', color:{argb:'FFCCCCCC'}} };
                });

                // Fila de Torres completadas (conteos de fecha y sumas productivas).
                const totalsRow = worksheet.addRow(totalsRowData);
                totalsRow.eachCell(cell => {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F0F8' } }; // Mismo fondo claro de la UI
                    cell.font = { bold: true, color: { argb: 'FF004F86' } }; // Letra azul de la UI
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    // Bordes dobles/gruesos arriba y abajo para separarla como en la UI
                    cell.border = { top: {style:'thin', color:{argb:'FFCCCCCC'}}, left: {style:'thin', color:{argb:'FFCCCCCC'}}, bottom: {style:'medium', color:{argb:'FF999999'}}, right: {style:'thin', color:{argb:'FFCCCCCC'}} };
                });

                // Números de fila y rango de datos (los datos van debajo de la fila de totales).
                const countsNum = totalsRow.number; // fila de conteos/sumas (abajo)
                const pctNum = pctRow.number;         // fila de % (arriba)
                const dataStart = countsNum + 1;
                const dataEnd = countsNum + filteredControlProduction.length;
                const totalCell = `$B$${countsNum}`;

                // Total de torres: solo las que poseen "peso torre" (tower_weight) > 0.
                // Se deja como valor estático porque el peso no se exporta como columna,
                // por lo que un COUNTA sobre la columna Torre contaría todas las torres.
                totalsRow.getCell(2).value = towerCount;

                // Torres completadas (=CONTARA) y % de avance por columna de fecha (Inicio y Término).
                activityCols.forEach((ac) => {
                    [ac.inicioCol, ac.terminoCol].forEach((col) => {
                        const L = colLetter(col);
                        totalsRow.getCell(col).value = { formula: `COUNTA(${L}${dataStart}:${L}${dataEnd})` };
                        const pc = pctRow.getCell(col);
                        pc.value = { formula: `IF(${totalCell}=0,0,${L}${countsNum}/${totalCell})` };
                        pc.numFmt = '0.0%';
                        pc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F0F8' } };
                        pc.font = { bold: true, color: { argb: 'FF004F86' } };
                        pc.alignment = { horizontal: 'center', vertical: 'middle' };
                        pc.border = { top: {style:'thin', color:{argb:'FFCCCCCC'}}, left: {style:'thin', color:{argb:'FFCCCCCC'}}, bottom: {style:'medium', color:{argb:'FF999999'}}, right: {style:'thin', color:{argb:'FFCCCCCC'}} };
                    });

                    // Columnas con valores productivos (Teórico, Real, VNT, VAflora): SUMA de los valores.
                    ac.numericCols.forEach((col) => {
                        const L = colLetter(col);
                        const sc = totalsRow.getCell(col);
                        sc.value = { formula: `SUM(${L}${dataStart}:${L}${dataEnd})` };
                        sc.numFmt = '#,##0.00';
                    });
                });

                // AVANCE ACUMULADO POR FASE (categoría validada): Σ(%avance Término × peso) / Σ(peso).
                // Se muestra como un bloque de celdas a partir de la columna E (una por fase con peso).
                let phaseCol = BASE_COLS + 1; // empieza en columna E
                const PHASE_CELL_WIDTH = 6;
                phaseGroups.forEach((g) => {
                    const startCell = `${colLetter(phaseCol)}${rollupRow.number}`;
                    const endCell = `${colLetter(phaseCol + PHASE_CELL_WIDTH - 1)}${rollupRow.number}`;
                    worksheet.mergeCells(`${startCell}:${endCell}`);
                    const cell = worksheet.getCell(startCell);
                    const sumWd = g.cols.reduce((s, ac) => s + Number(ac.column.weightDistribution || 0), 0);
                    const terms = g.cols
                        .map((ac) => `${colLetter(ac.terminoCol)}${pctNum}*${Number(ac.column.weightDistribution)}`)
                        .join('+');
                    const label = g.label.toUpperCase().replace(/"/g, '');
                    cell.value = { formula: `"${label}: "&TEXT((${terms})/${sumWd},"0.0%")` };
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDEBF7' } };
                    cell.font = { bold: true, color: { argb: 'FF004F86' } };
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = { top: {style:'thin', color:{argb:'FF999999'}}, left: {style:'thin', color:{argb:'FF999999'}}, bottom: {style:'thin', color:{argb:'FF999999'}}, right: {style:'thin', color:{argb:'FF999999'}} };
                    phaseCol += PHASE_CELL_WIDTH;
                });
            }
            // --- FIN FILA DE TOTALES EN EXCEL ---

            filteredControlProduction.forEach((tower) => {
                const getNum = (val: any) => {
                    if (val !== null && val !== undefined && val !== "") {
                        const parsed = parseSafeFloat(val);
                        return Number(parsed.toFixed(2));
                    }
                    return "";
                };

                const rowData: any[] = [
                    tower.vertexName,
                    tower.towerNumber,
                    tower.towerType,
                    getNum(tower.forwardSpanTeoric)
                ];

                activitiesProject.forEach((column) => {
                    const uniqueKey = `${tower.towerNumber}-${column.id}`;
                    const cellData = processedDataMap.get(uniqueKey);
                    const type = cellData?.type || getActivityType(column.activity);

                    switch (type) {
                        case 'HORMIGONADO':
                            rowData.push(
                                formatDate(cellData?.startDate), formatDate(cellData?.endDate),
                                getNum(cellData?.finalTeoric), getNum(cellData?.realHormigonado),
                                getNum(cellData?.totalVnt), getNum(cellData?.totalOutcrop),
                                cellData?.frontLeader?.join(", ") || ""
                            );
                            break;
                        case 'STANDARD_REAL':
                            rowData.push(
                                formatDate(cellData?.startDate), formatDate(cellData?.endDate),
                                getNum(cellData?.finalTeoric), getNum(cellData?.realToDisplay)
                            );
                            break;
                        case 'NO_REAL':
                            rowData.push(formatDate(cellData?.startDate), formatDate(cellData?.endDate), getNum(cellData?.finalTeoric));
                            break;
                        case 'WITH_LEADER':
                            rowData.push(formatDate(cellData?.startDate), formatDate(cellData?.endDate), getNum(cellData?.finalTeoric), cellData?.frontLeader?.join(", ") || "");
                            break;
                        case 'LEADER_NO_TEORIC':
                            rowData.push(formatDate(cellData?.startDate), formatDate(cellData?.endDate), cellData?.frontLeader?.join(", ") || "");
                            break;
                        default:
                            rowData.push(formatDate(cellData?.startDate), formatDate(cellData?.endDate));
                            break;
                    }
                });

                // Escribimos las celdas vacías como null (no como "") para que CONTARA/COUNTA
                // no las cuente como registros. ExcelJS guarda "" como celda de texto no vacía.
                const newRow = worksheet.addRow(rowData.map((v) => (v === "" ? null : v)));
                newRow.eachCell(cell => {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };
                    cell.border = { top: {style:'thin', color: {argb:'FFEEEEEE'}}, left: {style:'thin', color: {argb:'FFEEEEEE'}}, bottom: {style:'thin', color: {argb:'FFEEEEEE'}}, right: {style:'thin', color: {argb:'FFEEEEEE'}} };
                });

                // Relleno por semana en las fechas de Inicio y Término (amarillo = actual, azul = anterior).
                activityCols.forEach((ac) => {
                    const cellData = processedDataMap.get(`${tower.towerNumber}-${ac.column.id}`);
                    const bStart = weekBucket(cellData?.startDate);
                    const bEnd = weekBucket(cellData?.endDate);
                    if (bStart) newRow.getCell(ac.inicioCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bStart === 'current' ? SOFT_YELLOW : SOFT_BLUE } };
                    if (bEnd) newRow.getCell(ac.terminoCol).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bEnd === 'current' ? SOFT_YELLOW : SOFT_BLUE } };
                });
            });

            worksheet.columns.forEach(column => {
                column.width = 15;
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `control_produccion_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" marginBottom={3} color="black" fontWeight="bold">Control de producción</Typography>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Proyecto</InputLabel>
                        <Select label="Proyecto" name="project" value={selectedProject} onChange={handleProjectChange}>
                            {projects.map((project) => (<MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Autocomplete
                        size="small"
                        disabled={!towersList.length}
                        options={towersList.map((tower) => tower.towerNumber)}
                        value={selectedTower}
                        onChange={handleTowerChange}
                        renderInput={(params) => <TextField {...params} label="Torre" />}
                        noOptionsText="No se encontraron torres"
                    />
                </Grid>
            </Grid>

            {!loading && phaseProgress.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#004F86', mr: 1 }}>
                        Avance acumulado por fase:
                    </Typography>
                    {phaseProgress.map((p) => (
                        <Paper key={p.label} elevation={0} sx={{ px: 1.5, py: 0.5, backgroundColor: '#DDEBF7', border: '1px solid #b8d0e8', borderRadius: 1 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 'bold', color: '#004F86' }}>
                                {p.label}: {(p.pct * 100).toFixed(1)}%
                            </Typography>
                        </Paper>
                    ))}
                </Box>
            )}

            <Box display="flex" mt={2}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="300px"><CircularProgress /></Box>
                ) : (
                    <TableContainer component={Paper} sx={{ width: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 220px)', flexGrow: 1 }}>
                        <Table stickyHeader size="small" sx={{ borderCollapse: "collapse" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell rowSpan={4} align="center" style={{ ...tableStyles.headerFont, ...tableStyles.stickyColumnHead, top: 0, left: COL_POS.vertex, width: COL_WIDTHS.vertex, minWidth: COL_WIDTHS.vertex, maxWidth: COL_WIDTHS.vertex }}>Vértice</TableCell>
                                    <TableCell rowSpan={4} align="center" style={{ ...tableStyles.headerFont, ...tableStyles.stickyColumnHead, top: 0, left: COL_POS.tower, width: COL_WIDTHS.tower, minWidth: COL_WIDTHS.tower, maxWidth: COL_WIDTHS.tower }}>Torre</TableCell>
                                    <TableCell rowSpan={4} align="center" style={{ ...tableStyles.headerFont, ...tableStyles.stickyColumnHead, top: 0, left: COL_POS.type, width: COL_WIDTHS.type, minWidth: COL_WIDTHS.type, maxWidth: COL_WIDTHS.type }}>Tipo Torre</TableCell>
                                    <TableCell rowSpan={4} align="center" style={{ ...tableStyles.headerFont, ...tableStyles.stickyColumnHead, top: 0, left: COL_POS.span, width: COL_WIDTHS.span, minWidth: COL_WIDTHS.span, maxWidth: COL_WIDTHS.span, borderRight: "2px solid #999", zIndex: 11 }}>Vano Adelante</TableCell>

                                    {sectionHeaders.map((section, idx) => (
                                        <TableCell key={idx} colSpan={section.colSpan} align="center" style={{ ...tableStyles.headerFont, backgroundColor: "#3f51b5", color: "#fff", borderRight: "1px solid #ccc", position: "sticky", top: 0, height: "25px", zIndex: 5 }}>{section.name}</TableCell>
                                    ))}
                                </TableRow>

                                <TableRow>
                                    {activityHeaders.map((group, index) => (
                                        <TableCell key={`${group.id}-${index}`} colSpan={group.colSpan} align="center" style={{ ...tableStyles.headerFont, backgroundColor: "#78a1bf", color: "#004F86", borderLeft: "1px solid #ccc", textAlign: "center", position: "sticky", top: "25px", height: "40px", zIndex: 4 }}>{group.name}</TableCell>
                                    ))}
                                </TableRow>

                                <TableRow>
                                    {activitiesProject.map((column, index) => {
                                        const span = getColSpan(column.activity);
                                        return (
                                            <TableCell key={`sub-${index}`} colSpan={span} align="center"
                                                style={{ ...tableStyles.headerFont, backgroundColor: "#e0e0e0", color: "#000", borderLeft: "1px solid #bbb", textAlign: "center", position: "sticky", top: "65px", height: "30px", zIndex: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "150px" }}>
                                                {column.activity.subActivity || "-"}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>

                                <TableRow>
                                    {activitiesProject.map((column, index) => {
                                        const type = getActivityType(column.activity);
                                        const unit = normalizeUnit(column.activity.unitOfMeasurement);
                                        let displayUnit = unit;
                                        if (column.activity.name === "Tendido OPGW") displayUnit = "km";

                                        const actNameClean = cleanText(column.activity.name);
                                        const isMontaje = actNameClean.includes("montaje");
                                        const isExcavation = actNameClean.includes("excavaci");
                                        const isRoads = actNameClean.includes("camino") || actNameClean.includes("acceso");

                                        const baseStyle = { ...tableStyles.headerFont, borderRight: "1px solid #ccc", backgroundColor: "#d3d3d3", textAlign: "center" as const, position: "sticky" as const, top: "95px", height: "30px", zIndex: 4 };
                                        const dateStyle = { ...baseStyle, minWidth: '85px' };
                                        const numStyle = { ...baseStyle, minWidth: '65px' };

                                        switch (type) {
                                            case 'HORMIGONADO':
                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell style={dateStyle}>Fecha de Inicio</TableCell>
                                                        <TableCell style={dateStyle}>Fecha de Término</TableCell>
                                                        <TableCell style={numStyle}>Teórico ({displayUnit})</TableCell>
                                                        <TableCell style={numStyle}>Real (m³)</TableCell>
                                                        <TableCell style={numStyle}>V.N.T (m³)</TableCell>
                                                        <TableCell style={numStyle}>V.Aflora (m³)</TableCell>
                                                        <TableCell style={{...baseStyle, minWidth: '80px'}}>Realizó</TableCell>
                                                    </React.Fragment>
                                                );
                                            case 'NO_REAL':
                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell style={dateStyle}>Fecha de Inicio</TableCell>
                                                        <TableCell style={dateStyle}>Fecha de Término</TableCell>
                                                        <TableCell style={numStyle}>Teórico ({displayUnit})</TableCell>
                                                    </React.Fragment>
                                                );
                                            case 'WITH_LEADER':
                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell style={dateStyle}>Fecha de Inicio</TableCell>
                                                        <TableCell style={dateStyle}>Fecha de Término</TableCell>
                                                        <TableCell style={numStyle}>{isMontaje ? 'Peso (tn)' : `Teórico (${displayUnit})`}</TableCell>
                                                        <TableCell style={{...baseStyle, minWidth: '80px'}}>Realizó</TableCell>
                                                    </React.Fragment>
                                                );
                                            case 'LEADER_NO_TEORIC':
                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell style={dateStyle}>Fecha de Inicio</TableCell>
                                                        <TableCell style={dateStyle}>Fecha de Término</TableCell>
                                                        <TableCell style={{...baseStyle, minWidth: '80px'}}>Realizó</TableCell>
                                                    </React.Fragment>
                                                );
                                            case 'STANDARD_REAL':
                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell style={dateStyle}>Fecha de Inicio</TableCell>
                                                        <TableCell style={dateStyle}>Fecha de Término</TableCell>
                                                        <TableCell style={numStyle}>Teórico ({displayUnit})</TableCell>
                                                        <TableCell style={numStyle}>{(isRoads ? 'Real (m)' : (isExcavation ? 'Real (m³)' : 'Real'))}</TableCell>
                                                    </React.Fragment>
                                                );
                                            default:
                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableCell style={dateStyle}>Fecha de Inicio</TableCell>
                                                        <TableCell style={dateStyle}>Fecha de Término</TableCell>
                                                    </React.Fragment>
                                                );
                                        }
                                    })}
                                </TableRow>

                                {/* --- FILA DE % DE AVANCE (arriba: torres con fecha / total de torres) --- */}
                                {filteredControlProduction.length > 0 && (() => {
                                    const total = filteredControlProduction.length;
                                    const pct = (n: number) => (total ? `${((Number(n || 0) / total) * 100).toFixed(1)}%` : "0%");
                                    const pctSx = { ...tableStyles.headerFont, borderRight: "1px solid #ccc", borderTop: "2px solid #999", fontWeight: 'bold', color: '#0a7d33', backgroundColor: '#eef7ef', position: 'sticky' as const, top: '125px', zIndex: 5, height: "30px" };
                                    const stickyPctSx = { ...tableStyles.headerFont, backgroundColor: '#eef7ef', color: '#0a7d33', borderTop: "2px solid #999", position: 'sticky' as const, top: '125px', zIndex: 12, height: "30px" };
                                    return (
                                        <TableRow>
                                            <TableCell align="center" style={{ ...stickyPctSx, left: COL_POS.vertex }}>% Avance</TableCell>
                                            <TableCell align="center" style={{ ...stickyPctSx, left: COL_POS.tower }}></TableCell>
                                            <TableCell align="center" style={{ ...stickyPctSx, left: COL_POS.type }}></TableCell>
                                            <TableCell align="center" style={{ ...stickyPctSx, left: COL_POS.span, borderRight: "2px solid #999", zIndex: 13 }}></TableCell>

                                            {activitiesProject.map((column) => {
                                                const counts = columnCounts.get(column.id) || {};
                                                const type = counts.type || getActivityType(column.activity);
                                                const ini = <TableCell align="center" style={pctSx}>{pct(counts.startCount)}</TableCell>;
                                                const fin = <TableCell align="center" style={pctSx}>{pct(counts.endCount)}</TableCell>;
                                                const blank = (k: string) => <TableCell align="center" style={pctSx} key={k}></TableCell>;
                                                switch (type) {
                                                    case 'HORMIGONADO':
                                                        return (<React.Fragment key={column.id}>{ini}{fin}{blank('t')}{blank('r')}{blank('v')}{blank('a')}{blank('l')}</React.Fragment>);
                                                    case 'NO_REAL':
                                                        return (<React.Fragment key={column.id}>{ini}{fin}{blank('t')}</React.Fragment>);
                                                    case 'WITH_LEADER':
                                                        return (<React.Fragment key={column.id}>{ini}{fin}{blank('t')}{blank('l')}</React.Fragment>);
                                                    case 'LEADER_NO_TEORIC':
                                                        return (<React.Fragment key={column.id}>{ini}{fin}{blank('l')}</React.Fragment>);
                                                    case 'STANDARD_REAL':
                                                        return (<React.Fragment key={column.id}>{ini}{fin}{blank('t')}{blank('r')}</React.Fragment>);
                                                    default:
                                                        return (<React.Fragment key={column.id}>{ini}{fin}</React.Fragment>);
                                                }
                                            })}
                                        </TableRow>
                                    );
                                })()}

                                {/* --- FILA DE TORRES COMPLETADAS (abajo) --- */}
                                {filteredControlProduction.length > 0 && (
                                    <TableRow>
                                        <TableCell align="center" style={{ ...tableStyles.headerFont, backgroundColor: '#e8f0f8', color: '#004F86', borderTop: "2px solid #999", borderBottom: "2px solid #999", position: 'sticky', top: '155px', left: COL_POS.vertex, zIndex: 12, height: "30px" }}>
                                            {vertexCount}
                                        </TableCell>
                                        <TableCell align="center" style={{ ...tableStyles.headerFont, backgroundColor: '#e8f0f8', color: '#004F86', borderTop: "2px solid #999", borderBottom: "2px solid #999", position: 'sticky', top: '155px', left: COL_POS.tower, zIndex: 12, height: "30px" }}>
                                            {towerCount}
                                        </TableCell>
                                        <TableCell align="center" style={{ ...tableStyles.headerFont, backgroundColor: '#e8f0f8', color: '#004F86', borderTop: "2px solid #999", borderBottom: "2px solid #999", position: 'sticky', top: '155px', left: COL_POS.type, zIndex: 12, height: "30px" }}>
                                        </TableCell>
                                        <TableCell align="center" style={{ ...tableStyles.headerFont, backgroundColor: '#e8f0f8', color: '#004F86', borderTop: "2px solid #999", borderBottom: "2px solid #999", position: 'sticky', top: '155px', left: COL_POS.span, borderRight: "2px solid #999", zIndex: 13, height: "30px" }}>
                                            {formatNumber(spanTotal)}
                                        </TableCell>

                                        {activitiesProject.map((column) => {
                                            const counts = columnCounts.get(column.id) || {};
                                            const type = counts.type || getActivityType(column.activity);
                                            const cellSx = { ...tableStyles.headerFont, borderRight: "1px solid #ccc", borderTop: "2px solid #999", borderBottom: "2px solid #999", fontWeight: 'bold', color: '#004F86', backgroundColor: '#e8f0f8', position: 'sticky' as const, top: '155px', zIndex: 5, height: "30px" };

                                            switch (type) {
                                                case 'HORMIGONADO':
                                                    return (
                                                        <React.Fragment key={column.id}>
                                                            <TableCell align="center" style={cellSx}>{counts.startCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.endCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.teoricSum || 0, 2)}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.realSum || 0, 2)}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.vntSum || 0, 2)}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.afloraSum || 0, 2)}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.leaderCount || 0}</TableCell>
                                                        </React.Fragment>
                                                    );
                                                case 'NO_REAL':
                                                    return (
                                                        <React.Fragment key={column.id}>
                                                            <TableCell align="center" style={cellSx}>{counts.startCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.endCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.teoricSum || 0, 2)}</TableCell>
                                                        </React.Fragment>
                                                    );
                                                case 'WITH_LEADER':
                                                    return (
                                                        <React.Fragment key={column.id}>
                                                            <TableCell align="center" style={cellSx}>{counts.startCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.endCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.teoricSum || 0, 2)}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.leaderCount || 0}</TableCell>
                                                        </React.Fragment>
                                                    );
                                                case 'LEADER_NO_TEORIC':
                                                    return (
                                                        <React.Fragment key={column.id}>
                                                            <TableCell align="center" style={cellSx}>{counts.startCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.endCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.leaderCount || 0}</TableCell>
                                                        </React.Fragment>
                                                    );
                                                case 'STANDARD_REAL':
                                                    return (
                                                        <React.Fragment key={column.id}>
                                                            <TableCell align="center" style={cellSx}>{counts.startCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.endCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.teoricSum || 0, 2)}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{formatNumber(counts.realSum || 0, 2)}</TableCell>
                                                        </React.Fragment>
                                                    );
                                                default:
                                                    return (
                                                        <React.Fragment key={column.id}>
                                                            <TableCell align="center" style={cellSx}>{counts.startCount || 0}</TableCell>
                                                            <TableCell align="center" style={cellSx}>{counts.endCount || 0}</TableCell>
                                                        </React.Fragment>
                                                    );
                                            }
                                        })}
                                    </TableRow>
                                )}
                            </TableHead>

                            <TableBody>
                                {filteredControlProduction.map((tower, index) => {
                                    return (
                                        <TableRow key={tower.id || index} ref={(el) => (rightTableRefs.current[index] = el)} hover>
                                            <TableCell align="center" sx={{ ...tableStyles.bodyFont, ...tableStyles.stickyColumnBody, left: COL_POS.vertex, width: COL_WIDTHS.vertex, minWidth: COL_WIDTHS.vertex, maxWidth: COL_WIDTHS.vertex }}>{tower.vertexName}</TableCell>
                                            <TableCell align="center" sx={{ ...tableStyles.bodyFont, ...tableStyles.stickyColumnBody, left: COL_POS.tower, width: COL_WIDTHS.tower, minWidth: COL_WIDTHS.tower, maxWidth: COL_WIDTHS.tower }}>{tower.towerNumber}</TableCell>
                                            <TableCell align="center" sx={{ ...tableStyles.bodyFont, ...tableStyles.stickyColumnBody, left: COL_POS.type, width: COL_WIDTHS.type, minWidth: COL_WIDTHS.type, maxWidth: COL_WIDTHS.type }}>{tower.towerType}</TableCell>
                                            <TableCell align="center" sx={{ ...tableStyles.bodyFont, ...tableStyles.stickyColumnBody, left: COL_POS.span, width: COL_WIDTHS.span, minWidth: COL_WIDTHS.span, maxWidth: COL_WIDTHS.span, borderRight: "2px solid #999", zIndex: 10 }}>{formatNumber(tower.forwardSpanTeoric)}</TableCell>

                                            {activitiesProject.map((column) => {
                                                const uniqueKey = `${tower.towerNumber}-${column.id}`;
                                                const cellData = processedDataMap.get(uniqueKey);

                                                const cellSx = { ...tableStyles.bodyFont, borderRight: "1px solid #eee", textAlign: 'center' as const };
                                                // Color por semana (igual que el Excel): actual=amarillo, anterior=azul.
                                                const startBg = weekColorOf(cellData?.startDate) || 'inherit';
                                                const endBg = weekColorOf(cellData?.endDate) || 'inherit';
                                                const type = cellData?.type;

                                                switch (type) {
                                                    case 'HORMIGONADO':
                                                        return (
                                                            <React.Fragment key={column.id}>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: startBg }}>
                                                                    {formatDate(cellData?.startDate)}
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: endBg }}>{formatDate(cellData?.endDate)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{formatNumber(cellData?.finalTeoric)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>
                                                                    {formatNumber(cellData?.realHormigonado)}
                                                                </TableCell>
                                                                <TableCell align="center" sx={cellSx}>{formatNumber(cellData?.totalVnt)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{formatNumber(cellData?.totalOutcrop)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{cellData?.frontLeader?.join(", ") || ""}</TableCell>
                                                            </React.Fragment>
                                                        );
                                                    case 'NO_REAL':
                                                        return (
                                                            <React.Fragment key={column.id}>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: startBg }}>{formatDate(cellData?.startDate)}</TableCell>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: endBg }}>{formatDate(cellData?.endDate)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{formatNumber(cellData?.finalTeoric)}</TableCell>
                                                            </React.Fragment>
                                                        );
                                                    case 'WITH_LEADER':
                                                        return (
                                                            <React.Fragment key={column.id}>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: startBg }}>{formatDate(cellData?.startDate)}</TableCell>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: endBg }}>{formatDate(cellData?.endDate)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{formatNumber(cellData?.finalTeoric)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{cellData?.frontLeader?.join(", ") || ""}</TableCell>
                                                            </React.Fragment>
                                                        );
                                                    case 'LEADER_NO_TEORIC':
                                                        return (
                                                            <React.Fragment key={column.id}>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: startBg }}>{formatDate(cellData?.startDate)}</TableCell>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: endBg }}>{formatDate(cellData?.endDate)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{cellData?.frontLeader?.join(", ") || ""}</TableCell>
                                                            </React.Fragment>
                                                        );
                                                    case 'STANDARD_REAL':
                                                        return (
                                                            <React.Fragment key={column.id}>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: startBg }}>{formatDate(cellData?.startDate)}</TableCell>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: endBg }}>{formatDate(cellData?.endDate)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>{formatNumber(cellData?.finalTeoric, cellData?.isRoads ? 0 : 2)}</TableCell>
                                                                <TableCell align="center" sx={cellSx}>
                                                                    {(cellData?.realToDisplay !== null && cellData?.realToDisplay !== undefined && cellData?.realToDisplay !== "")
                                                                        ? formatNumber(cellData?.realToDisplay, cellData?.isExcavation ? 2 : (cellData?.isRoads ? 0 : 2))
                                                                        : ""}
                                                                </TableCell>
                                                            </React.Fragment>
                                                        );
                                                    default:
                                                        return (
                                                            <React.Fragment key={column.id}>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: startBg }}>{formatDate(cellData?.startDate)}</TableCell>
                                                                <TableCell align="center" sx={{ ...cellSx, backgroundColor: endBg }}>{formatDate(cellData?.endDate)}</TableCell>
                                                            </React.Fragment>
                                                        );
                                                }
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
            {selectedProject && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="contained" color="primary" onClick={exportToExcel} style={{ marginBottom: "20px" }} disabled={userRole === profiles[3].name}>Exportar informe</Button>
                </Box>
            )}
        </Box>
    );
}

export default ProductionControl;